import { call, put, takeEvery, getContext } from "redux-saga/effects";

import {
  signInSuccess,
  signInFailure,
  signInReset,
  toggleSignInModal,
} from "./actions";
import { setProfile } from "../profile/actions";
import {
  signInActionTypes,
  signInGoogle,
  signInMetamask,
  signInTwitch,
  signInTwitter,
} from "./actionTypes";
import { prepareMetamask } from "@helpers/metamask";
import { CONTEXT } from "@const/redux";
import { localstorageKeys } from "@const/store";
import { socialAuthTypes, usernameSignIn } from "@const/socials";
import { removeEmptyKeys } from "@helpers/common";
import { ErrorCodes } from "@const/errors";
import { toggleTwoFactorsModal } from "../twoFactors/actions";
import ToastService from "../../services/ToastService";
import { signUpReset } from "../signUp/actions";
import { toggleAddEmailModal } from "../addEmail/actions";
import storageService from "../../services/StorageService";

const signInWithUsernameAndPasswordAsync = ({
  SessionsService,
  email,
  password,
}) => {
  if (!SessionsService) {
    console.log("Required services does not exist");
    return null;
  }

  return SessionsService.createSession({
    email,
    password,
  });
};

function* handle2FaAndEmailSaga({
  data,
  StorageService,
  signInWith,
  isFirstLogin,
}) {
  const { user, session } = data;

  if (user.isVerified) {
    if (user.is2FAEnabled) {
      StorageService.set(localstorageKeys.authSession, session);
      yield put(signInReset());
      yield put(toggleTwoFactorsModal(true));
    } else {
      StorageService.set(localstorageKeys.session, session);
      yield put(setProfile(user));
      storageService.set(
        localstorageKeys.tourFinished,
        user.isProfileTourCompleted,
      );
      yield put(
        signInSuccess({
          signInWith,
          isFirstLogin,
        }),
      );
    }
  } else {
    StorageService.set(localstorageKeys.authSession, session);

    if (user.email) {
      yield put(
        signInFailure({
          email: user.email,
          message: "Please verify your email",
          code: ErrorCodes.EMAIL_NOT_VERIFIED,
        }),
      );
      yield put(toggleSignInModal(true));
    } else {
      yield put(signInReset());
      yield put(signUpReset());
      yield put(toggleAddEmailModal(true));
    }
  }
}

function* signIn({ payload: { email, password } }) {
  ToastService.clearAll();
  try {
    const { SessionsService, StorageService } = yield getContext(
      CONTEXT.SERVICES,
    );
    const { data } = yield call(signInWithUsernameAndPasswordAsync, {
      SessionsService,
      email,
      password,
    });
    yield call(handle2FaAndEmailSaga, {
      data,
      StorageService,
      signInWith: usernameSignIn,
      isFirstLogin: data?.user?.isFirstLogin,
    });
  } catch ({ error }) {
    yield put(signInFailure(error));
  }
}

const signInWithMetamaskAsync = ({
  SocialsService,
  address,
  signature,
  referral,
  trackingCode,
}) => {
  if (!SocialsService) {
    console.log("Required services does not exist");

    return null;
  }

  const requestBody = removeEmptyKeys({
    address,
    signature,
    referral,
    trackingCode,
  });

  return SocialsService.loginWithMetamask(requestBody);
};

function* signInWithMetamask({ payload: { referral, trackingCode } }) {
  try {
    const skipAuth = true;
    const { SocialsService, StorageService } = yield getContext(
      CONTEXT.SERVICES,
    );

    const { address, signature } = yield call(prepareMetamask, skipAuth);
    const { data } = yield call(signInWithMetamaskAsync, {
      SocialsService,
      address,
      signature,
      referral,
      trackingCode,
    });

    const { isLogin } = data;

    yield call(handle2FaAndEmailSaga, {
      data,
      StorageService,
      signInWith: isLogin
        ? socialAuthTypes.metamaskSignIn
        : socialAuthTypes.metamaskSignUp,
      isFirstLogin: data?.user?.isFirstLogin,
    });
  } catch ({ error }) {
    yield put(signInFailure(error));
  }
}

const signInWithGoogleAsync = ({
  SocialsService,
  token,
  trackingCode,
  referral,
  link,
}) => {
  if (!SocialsService) {
    console.log("Required services does not exist");

    return null;
  }
  const requestBody = removeEmptyKeys({
    token,
    referral,
    link,
    trackingCode,
  });

  return SocialsService.loginWithGoogle(requestBody);
};

const signInWithTwitchAsync = ({
  SocialsService,
  token,
  referral,
  trackingCode,
}) => {
  if (!SocialsService) {
    console.log("Required services does not exist");

    return null;
  }

  const requestBody = removeEmptyKeys({
    token,
    referral,
    trackingCode,
  });

  return SocialsService.loginWithTwitch(requestBody);
};

const signInWithTwitterAsync = ({
  SocialsService,
  token,
  verifierToken,
  referral,
  trackingCode,
}) => {
  if (!SocialsService) {
    console.log("Required services does not exist");

    return null;
  }

  const requestBody = removeEmptyKeys({
    token,
    verifierToken,
    referral,
    trackingCode,
  });

  return SocialsService.loginWithTwitter(requestBody);
};

function* signInWithGoogle({
  payload: { token, referral, trackingCode, link },
}) {
  try {
    const { SocialsService, StorageService } = yield getContext(
      CONTEXT.SERVICES,
    );

    const { data } = yield call(signInWithGoogleAsync, {
      SocialsService,
      token,
      referral,
      trackingCode,
      link,
    });
    const { isLogin } = data;

    yield call(handle2FaAndEmailSaga, {
      data,
      StorageService,
      signInWith: isLogin
        ? socialAuthTypes.googleSignIn
        : socialAuthTypes.googleSignUp,
      isFirstLogin: data?.user?.isFirstLogin,
    });
  } catch ({ error }) {
    yield put(signInFailure(error));
  }
}

function* signInWithTwitch({ payload: { token, referral, trackingCode } }) {
  try {
    const { SocialsService, StorageService } = yield getContext(
      CONTEXT.SERVICES,
    );

    const { data } = yield call(signInWithTwitchAsync, {
      SocialsService,
      token,
      referral,
      trackingCode,
    });

    const { isLogin } = data;

    yield call(handle2FaAndEmailSaga, {
      data,
      StorageService,
      signInWith: isLogin
        ? socialAuthTypes.googleSignIn
        : socialAuthTypes.googleSignUp,
      isFirstLogin: data?.user?.isFirstLogin,
    });
  } catch ({ error }) {
    ToastService.error(error?.message);
    yield put(signInReset());
    window.history.pushState({}, "", "/");
  }
}

function* signInWithTwitter({
  payload: { token, referral, verifierToken, trackingCode },
}) {
  try {
    const { SocialsService, StorageService } = yield getContext(
      CONTEXT.SERVICES,
    );

    const { data } = yield call(signInWithTwitterAsync, {
      SocialsService,
      token,
      verifierToken,
      referral,
      trackingCode,
    });

    const { isLogin } = data;

    yield call(handle2FaAndEmailSaga, {
      data,
      StorageService,
      signInWith: isLogin
        ? socialAuthTypes.googleSignIn
        : socialAuthTypes.googleSignUp,
      isFirstLogin: data?.user?.isFirstLogin,
    });
  } catch ({ error }) {
    ToastService.error(error?.message);
    yield put(signInReset());
    window.history.pushState({}, "", "/");
  }
}

export function* watchSignIn() {
  yield takeEvery(signInActionTypes.request, signIn);
}

export function* watchMetamaskSignIn() {
  yield takeEvery(signInMetamask.request, signInWithMetamask);
}

export function* watchGoogleSignIn() {
  yield takeEvery(signInGoogle.request, signInWithGoogle);
}

export function* watchTwitchSignIn() {
  yield takeEvery(signInTwitch.request, signInWithTwitch);
}

export function* watchTwitterSignIn() {
  yield takeEvery(signInTwitter.request, signInWithTwitter);
}
